import { useRef, useState, type ReactNode } from 'react';
import { Home, Gamepad, Ticket, Users, LogOut, Menu, type LucideIcon, Layers2, Paintbrush, Images, User } from 'lucide-react';
import { Icon } from 'components/Icon';
import { Spacer } from 'components/Spacer';
import { Link, useLocation, useRouter } from '@tanstack/react-router';
import { useIsAdmin, useToken } from 'engine/api';
import { appConfig } from 'engine/appConfig';

interface DashboardLayoutProps {
	children: ReactNode;
}

export const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
	const router = useRouter();
	const { token, setToken } = useToken();
	const isAdmin = useIsAdmin();

	// TODO: use some less hacky method of doing this like a useLocalStorage() hook or something
	const [navExpanded, setNavExpanded] = useState(localStorage.getItem('nav-expanded') === 'true');

	const navbarRef = useRef<HTMLElement>(null);

	const toggleNav = () => {
		const navbar = navbarRef.current;
		if (!navbar) return;

		const fromW = navbar.clientWidth;
		const nextExpanded = !navExpanded;

		if (nextExpanded) {
			navbar.setAttribute('data-expanded', '');
		} else {
			navbar.removeAttribute('data-expanded');
		}
		localStorage.setItem('nav-expanded', '' + nextExpanded);
		setNavExpanded(nextExpanded);

		const toW = navbar.clientWidth;
		navbar.animate([{ width: fromW + 'px' }, { width: toW + 'px' }], {
			easing: 'ease-in-out',
			duration: 80,
		});
	};

	const closeNavOnMobile = () => {
		if (window.innerWidth < 768 && navExpanded) {
			toggleNav();
		}
	};

	return (
		<div className="relative flex h-screen flex-col bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300 md:flex-row">
			<nav
				nav-bar="true"
				ref={navbarRef}
				data-expanded={navExpanded ? '' : undefined}
				className="group/nav fixed z-10 flex w-full flex-col rounded-b-xl bg-white p-2 shadow-lg dark:bg-gray-800 md:relative md:h-full md:w-auto md:rounded-r-xl"
			>
				<div className="flex flex-row">
					<button
						onClick={toggleNav}
						className="mr-auto flex w-full rounded-md p-2 text-gray-900 hover:bg-gray-100 dark:text-gray-100 dark:hover:bg-gray-700 md:mb-3"
					>
						<Icon className="pointer-events-none size-5" icon={Menu} />
					</button>
					{token && (
						<Link
							to="/dashboard/users/$id"
							params={{ id: token.id }}
							className="data-[ group relative flex items-center gap-2 rounded-md border-t border-transparent p-2 text-sm text-gray-800 shadow-theme-600 transition-all md:hidden"
						>
							<div className={`flex size-5 items-center justify-center rounded-full border border-gray-200 bg-gray-100`}>
								<User className="size-3 text-gray-400" />
							</div>
							<span className="shrink whitespace-nowrap">{token.name}</span>
						</Link>
					)}
				</div>
				<div className="hidden flex-1 shrink flex-col group-data-[expanded]/nav:flex md:flex">
					<NavItem name="Dashboard" href="/dashboard" icon={Home} />
					{isAdmin && <NavItem name="Media" href="/dashboard/media" icon={Images} onNavigate={closeNavOnMobile} />}
					{isAdmin && appConfig.permissions.allowSkins && (
						<NavItem name="Skins" href="/dashboard/skins" icon={Paintbrush} onNavigate={closeNavOnMobile} />
					)}
					{isAdmin && <NavItem name="Games" href="/dashboard/games" icon={Gamepad} onNavigate={closeNavOnMobile} />}
					{isAdmin && appConfig.permissions.allowVariants && (
						<NavItem name="Variants" href="/dashboard/variants" icon={Layers2} onNavigate={closeNavOnMobile} />
					)}
					<NavItem name="Events" href="/dashboard/events" icon={Ticket} onNavigate={closeNavOnMobile} />
					{isAdmin && <NavItem name="Users" href="/dashboard/users" icon={Users} onNavigate={closeNavOnMobile} />}
					{/* <NavItem name="Settings" href="/dashboard/settings" icon={Settings} /> */}
					<Spacer />
					{token && (
						<Link
							to="/dashboard/users/$id"
							params={{ id: token.id }}
							className="data-[ dark:shadow-theme-500/70 dark:data-[active]:border-theme-500/30 group relative flex items-center gap-2 rounded-md border-t border-transparent p-2 text-sm text-gray-800 shadow-theme-600 transition-all hover:bg-gray-100 data-[active]:border-theme-100 data-[active]:bg-theme-500 data-[active]:text-theme-50 data-[active]:shadow-sm group-data-[expanded]/nav:px-3 dark:text-gray-300 dark:hover:bg-gray-700 dark:data-[active]:bg-theme-600 dark:data-[active]:text-theme-300"
						>
							<div className={`flex size-5 items-center justify-center rounded-full border border-gray-200 bg-gray-100`}>
								<User className="size-3 text-gray-400" />
							</div>
							<span className="hidden w-0 shrink overflow-hidden whitespace-nowrap group-data-[expanded]/nav:block group-data-[expanded]/nav:w-auto">
								{token.name}
							</span>
						</Link>
					)}

					<NavItem
						name="Sign Out"
						icon={LogOut}
						href="/signout"
						onClick={() => {
							// clear caches since we are signing out
							router.invalidate();
							setToken(undefined);
							router.navigate({ to: '/login' });
						}}
					/>
				</div>
			</nav>
			<div className="mt-5 flex min-w-0 flex-1 shrink flex-col md:mt-0">
				<div className="flex h-full flex-1 shrink flex-col gap-8 overflow-auto p-5 md:p-8">
					<div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400"></div>
					<div className="flex-1 shrink">{children}</div>
				</div>
			</div>
		</div>
	);
};

type NavItemProps = {
	icon: string | LucideIcon;
	name: string;
	href?: string; //LinkProps['to'];
	onClick?: () => void;
	onNavigate?: () => void;
};

function NavItem({ icon, name, onClick, href = '/dashboard', onNavigate }: NavItemProps) {
	const page = href;
	const loc = useLocation();

	return (
		<Link
			to={href}
			onClick={(e) => {
				if (onClick) {
					e.preventDefault();
					onClick();
				}
				if (onNavigate && typeof window !== 'undefined' && window.innerWidth < 768) {
					onNavigate();
				}
			}}
			tabIndex={0}
			role="link"
			data-active={
				page == '/dashboard' ? (loc.pathname == page ? true : undefined) : loc.pathname.startsWith(page) ? true : undefined
			}
			data-page={page}
			className="dark:shadow-theme-500/70 dark:data-[active]:border-theme-500/30 group relative flex cursor-pointer items-center gap-2 rounded-md border-t border-transparent p-2 text-sm text-gray-800 shadow-theme-600 transition-all hover:bg-gray-100 data-[active]:border-theme-100 data-[active]:bg-theme-500 data-[active]:text-theme-50 data-[active]:shadow-sm group-data-[expanded]/nav:px-3 dark:text-gray-300 dark:hover:bg-gray-700 dark:data-[active]:bg-theme-600 dark:data-[active]:text-theme-300"
			onDragStart={(e) => e.preventDefault()}
		>
			<Icon
				icon={icon}
				className="pointer-events-none size-5 shrink-0 p-[2px] text-gray-500 group-data-[active]:text-theme-100 dark:text-gray-400 dark:group-data-[active]:text-theme-400"
			/>
			<span className="hidden w-0 shrink overflow-hidden whitespace-nowrap group-data-[expanded]/nav:block group-data-[expanded]/nav:w-auto">
				{name}
			</span>
			<span className="before:bg-inherit pointer-events-none absolute -right-4 translate-x-full whitespace-nowrap rounded-md border border-gray-200 bg-white p-2 leading-none text-gray-800 opacity-0 drop-shadow-sm before:absolute before:left-0 before:top-1/2 before:block before:size-2 before:-translate-x-1/2 before:-translate-y-1/2 before:rotate-45 before:rounded-sm before:border before:border-gray-200 before:bg-white before:clip-half group-hover:opacity-100 group-data-[expanded]/nav:hidden dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100 dark:before:border-gray-700 dark:before:bg-gray-950">
				{name}
			</span>
		</Link>
	);
}

export default DashboardLayout;
